import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import { RowCheckbox } from './RowCheckbox';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Secondary line — an email, a city, anything that disambiguates duplicates. */
  hint?: string;
}

export interface ComboboxProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  /** Shown when nothing is selected. */
  placeholder?: string;
  /** Label for the "no selection" choice, e.g. "Unassigned". Omit to hide it. */
  emptyOptionLabel?: string;
  /** Placeholder inside the filter box. */
  searchPlaceholder?: string;
  /**
   * Provide to search server-side. Called debounced with the typed term; the
   * parent refreshes `options`. Without it, filtering is local to `options`.
   */
  onSearch?: (term: string) => void;
  loading?: boolean;
  /** Rendered under the list — e.g. "Showing the first 100 of 400". */
  footnote?: string;
  disabled?: boolean;
  invalid?: boolean;
  /**
   * A pinned row at the TOP of the list that CREATES a new option instead of
   * choosing one — the single-select twin of `MultiCombobox`'s `addNew` and of
   * `PickerField`'s "+ Add a new…" option, for a list whose missing row can be
   * created without leaving the form.
   *
   * It is a ROW rather than a button beside the list because that is the only
   * place a keyboard reaches: `Tab` inside the popover closes it, so anything
   * outside the listbox would be pointer-only. Arrow keys, Home/End and Enter
   * therefore walk it as index 0 and every option shifts down by one — see
   * `addNewRows` below, which is the whole of the arithmetic.
   *
   * ⚠️ It sits above `emptyOptionLabel` too, not merely above the fetched rows.
   * "Unassigned" is a *choice*; this is an *action*, and the action belongs at
   * the top where somebody who has just failed to find their row is looking.
   *
   * `onSelect` is called with the popover already closed and focus back on the
   * trigger, so a dialog opened from it records the trigger as the element to
   * restore focus to. It must NOT select anything — `onChange` is not fired, and
   * the caller selects the created row itself once it has an id.
   *
   * ⚠️ The row is rendered while `loading` too, deliberately: the offset is then
   * a constant for as long as the popover is open, and a keyboard position
   * cannot shift under the user when a slow list arrives. A caller that must
   * withhold it while a list is unread does so by passing `undefined` — and owes
   * the reader a sentence saying why, since a row that merely vanishes reads as
   * a control that broke.
   *
   * Additive: omitted renders no row and leaves every index and key press
   * exactly as it was.
   */
  addNew?: {
    /** Written like an action, e.g. `+ Add a new contact…`. */
    label: string;
    onSelect: () => void;
  };
  /**
   * A control strip between the filter box and the list, for narrowing WHAT IS
   * OFFERED — the line grids' Current / Previous project pills are the first and
   * only caller.
   *
   * ⚠️ **A FILTER, never a choice.** Everything the listbox can select stays in
   * the listbox: `addNew` is a ROW for exactly that reason, and this is not a
   * second way in. Putting a selectable thing here makes it invisible to
   * `aria-activedescendant`, which is how the list is navigated.
   *
   * ⚠️ **The caller owns the state and must keep it OUT of `value`.** Narrowing
   * the options must never blank the selection: if the current `value` is not in
   * `options`, the trigger falls back to `placeholder` and the row reads as
   * empty over a real stored value. Callers inject the held option into every
   * narrowing, labelled.
   *
   * ── KEYBOARD, WHICH IS WHY THIS IS NOT SIMPLY A `<div>` SOMEWHERE ───────────
   *
   * `Tab` inside the popover normally closes it, which would make anything here
   * pointer-only — the precise objection the `addNew` comment above raises
   * against a button beside the list. So **when a header is present, a forward
   * `Tab` from the filter box moves focus INTO it** rather than closing, and a
   * forward `Tab` out of the header closes as before. `Shift+Tab` from the
   * header returns to the filter box on its own.
   *
   * ⚠️ **Additive: omitted renders nothing and leaves every key press, index and
   * measurement exactly as it was.** The `Tab` change is gated on this prop
   * being present, so the five existing call sites are unaffected.
   */
  header?: React.ReactNode;
  /**
   * Pick list + type your own (contact titles, owner 2026-10-18). When the
   * filter box holds a term that matches no option label exactly
   * (case-insensitive), a `Use “term”` option is prepended whose VALUE is the
   * trimmed term, so `onChange` receives free text. The caller must put a
   * stored value that is not in the list into `options` itself, or the trigger
   * falls back to `placeholder`.
   *
   * Additive: only meaningful with local filtering (no `onSearch`); omitted
   * leaves every index and key press exactly as it was.
   */
  freeText?: boolean;
  'aria-describedby'?: string;
}

const POPOVER_MAX_HEIGHT = 288;

/**
 * The popover never narrows below this however narrow the trigger is — a ~100px
 * table cell would otherwise open a ~100px list of truncated names.
 */
const POPOVER_MIN_WIDTH = 240;

/** How close to the viewport edge the popover may sit, on either side. */
const VIEWPORT_MARGIN = 8;

const TABBABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

/**
 * Moves focus to the tabbable element after (or, with `backward`, before) the
 * trigger, as a native `<select>` would on Tab. Returns false when there is
 * nowhere to go, so the caller can park focus on the trigger instead.
 *
 * ⚠️ **Why this exists: the popover is portalled to the end of `<body>`.** A
 * Tab left to the browser continues from the filter box, which has nothing after
 * it in DOM order, so focus wrapped to the skip link (forward) or the last
 * control on the page (Shift+Tab) — measured on every screen, 2026-10-11.
 *
 * Inside a modal the walk is scoped to the dialog and WRAPS, because the
 * caller stops the Tab from reaching `Modal`'s own trap (which would otherwise
 * run a second time on the focus this has just moved).
 *
 * A byte-for-byte twin lives in `MultiCombobox.tsx`; the two must move together.
 */
function focusPastTrigger(
  trigger: HTMLElement | null,
  popover: HTMLElement | null,
  backward: boolean,
): boolean {
  if (!trigger) return false;
  const modal = trigger.closest<HTMLElement>('[aria-modal="true"]');
  const scope = modal ?? document.body;
  const items = Array.from(scope.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) =>
      el === trigger ||
      (el.tabIndex >= 0 &&
        !popover?.contains(el) &&
        !el.closest('[inert]') &&
        el.getClientRects().length > 0),
  );
  const index = items.indexOf(trigger);
  if (index === -1) return false;
  let target = backward ? items[index - 1] : items[index + 1];
  if (!target && modal) target = backward ? items.at(-1) : items[0];
  if (!target || target === trigger) return false;
  target.focus();
  return true;
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

/**
 * A searchable single-select.
 *
 * Shape is a button that opens a popover holding a filter box and a listbox —
 * not an editable combobox — because the field is a *choice*, and an input that
 * both displays a selection and accepts free text is the pattern users most
 * often end up fighting.
 *
 * The popover is portalled and positioned fixed. Rendering it inline would let
 * the modal's `overflow-y-auto` clip it, which is exactly where these pickers
 * live.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyOptionLabel,
  searchPlaceholder = 'Search…',
  onSearch,
  loading = false,
  footnote,
  disabled = false,
  invalid = false,
  addNew,
  header,
  freeText = false,
  'aria-describedby': describedBy,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState<{
    left: number;
    top: number;
    minWidth: number;
    maxWidth: number;
    flip: boolean;
  }>({
    left: 0,
    top: 0,
    minWidth: 0,
    maxWidth: 0,
    flip: false,
  });

  const listId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((option) => option.value === value);

  // With server-side search the parent has already filtered; filtering again
  // locally would hide results the server deliberately returned.
  const visible = useMemo(() => {
    const base = onSearch
      ? options
      : options.filter((option) =>
          `${option.label} ${option.hint ?? ''}`.toLowerCase().includes(term.trim().toLowerCase()),
        );
    const typed = term.trim();
    const custom: ComboboxOption[] =
      freeText &&
      typed !== '' &&
      !options.some((option) => option.label.trim().toLowerCase() === typed.toLowerCase())
        ? [{ value: typed, label: `Use “${typed}”` }]
        : [];
    // First, so Enter on a freshly typed term uses it.
    return emptyOptionLabel
      ? [...custom, { value: '', label: emptyOptionLabel }, ...base]
      : [...custom, ...base];
  }, [options, term, onSearch, emptyOptionLabel, freeText]);

  /**
   * 0 or 1. Every index below is `visible`'s index PLUS this, so the whole of
   * the shift lives in one number and the arithmetic collapses back to exactly
   * what it was when no row is offered.
   */
  const addNewRows = addNew ? 1 : 0;
  const rowCount = visible.length + addNewRows;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const below = window.innerHeight - box.bottom;
    const flip = below < POPOVER_MAX_HEIGHT + 16 && box.top > below;
    // Clamped horizontally as well as flipped vertically. The popover is
    // `fixed`, so a right-hand overhang is not merely ugly: it adds no page
    // scroll, so the part hanging off the edge can never be brought into view.
    // The options under it are silently unreachable. A narrow trigger forced up
    // to POPOVER_MIN_WIDTH near the right edge is the real case, not a
    // hypothetical one.
    //
    // The popover is CONTENT-sized (`width: max-content` below), not pinned to
    // the trigger, so a long option label is never truncated to a handful of
    // indistinguishable characters just because it opened out of a ~100px table
    // cell. `minWidth`/`maxWidth` are the two ends of what content-sizing is
    // allowed to land on: never below the existing floor (a short label must
    // not shrink the popover back to unreadable), never past the viewport
    // margins. What the browser actually renders in between is unknown until
    // layout runs — hence the measure pass below, rather than computing a
    // pixel width here from label lengths.
    const minWidth = Math.max(box.width, POPOVER_MIN_WIDTH);
    const maxWidth = Math.max(window.innerWidth - VIEWPORT_MARGIN * 2, 0);

    // ⚠️ Must MEASURE the popover rather than assume its width, and must stay
    // idempotent — it reruns on every scroll and resize. A stale provisional
    // width silently strands options past the viewport edge (measured at
    // 320px: left 30, rendered width 304, right edge 334 — `position: fixed`
    // means that overhang adds no page scroll to recover it). Reading the live
    // width here converges every call on one answer. Before the first render
    // there is nothing to measure, so `minWidth` is the fallback — never an
    // underestimate that pushes the popover off-screen — and the effect below
    // re-runs `place()` once the real box exists.
    const measured = popoverRef.current?.getBoundingClientRect().width ?? 0;
    const width = Math.min(Math.max(measured, minWidth), maxWidth);

    setRect((prev) => {
      const left = Math.max(
        VIEWPORT_MARGIN,
        // `innerWidth`, matching the vertical logic's `innerHeight` above
        // rather than introducing a second notion of "the viewport" here.
        Math.min(box.left, window.innerWidth - VIEWPORT_MARGIN - width),
      );
      const top = flip ? box.top : box.bottom;
      if (
        prev.left === left &&
        prev.top === top &&
        prev.minWidth === minWidth &&
        prev.maxWidth === maxWidth &&
        prev.flip === flip
      ) {
        return prev;
      }
      return { ...prev, left, top, minWidth, maxWidth, flip };
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  // Second pass, once the popover exists to be measured. `place()` runs before
  // the first render, when there is no box yet and it can only fall back to
  // `minWidth`; running it again now re-clamps against the real content width.
  //
  // It re-runs whenever the rendered content can change the width — `visible`
  // covers filtering the list, and the bounds cover scroll/resize/reopen. There
  // is no loop: `place()` bails out returning `prev` when nothing moved, so the
  // state identity is preserved and no further render is scheduled.
  useLayoutEffect(() => {
    if (!open || !popoverRef.current) return;
    place();
  }, [open, visible, place, rect.minWidth, rect.maxWidth, rect.top, rect.flip]);

  // The trigger can sit inside a scrolling modal, so follow it rather than
  // letting the popover detach and float over unrelated content.
  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Debounce server-side search so a request isn't fired per keystroke.
  useEffect(() => {
    if (!onSearch || !open) return;
    const timer = setTimeout(() => onSearch(term.trim()), 250);
    return () => clearTimeout(timer);
  }, [term, onSearch, open]);

  // Clamped during render rather than reset in an effect: when a server search
  // returns a shorter list the stored index can be past the end, and correcting
  // that here avoids an extra render pass and a highlight on nothing.
  const active = rowCount === 0 ? 0 : Math.min(activeIndex, rowCount - 1);
  /** Index 0 is the "add a new…" row when one is offered; options follow it. */
  const addNewIsActive = addNewRows === 1 && active === 0;
  const activeOption = visible[active - addNewRows];
  const realMatchCount = visible.filter((option) => option.value !== '').length;

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function close(returnFocus = true) {
    setOpen(false);
    setTerm('');
    if (returnFocus) triggerRef.current?.focus();
  }

  function commit(option: ComboboxOption) {
    onChange(option.value);
    close();
  }

  /** Tab out of the popover: close it and land where a native select would. */
  function tabOut(event: React.KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    close(false);
    if (!focusPastTrigger(triggerRef.current, popoverRef.current, event.shiftKey)) {
      triggerRef.current?.focus();
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      /*
       * The one row that closes WITHOUT selecting. Closing first is also what
       * parks focus back on the trigger, which a dialog raised by `onSelect`
       * then remembers as where to hand focus back.
       */
      if (addNew && addNewIsActive) {
        close();
        addNew.onSelect();
        return;
      }
      if (activeOption) commit(activeOption);
      return;
    }
    if (event.key === 'Tab') {
      /*
       * With a header present, a forward Tab hands focus to it instead of
       * closing — it is the next focusable in DOM order, so nothing is moved
       * here beyond declining to close. Without one, and on Shift+Tab, the
       * popover closes and focus moves past the trigger (`tabOut`).
       */
      if (header && !event.shiftKey) return;
      tabOut(event);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (rowCount === 0) return;
    setActiveIndex((current) => {
      if (event.key === 'Home') return 0;
      if (event.key === 'End') return rowCount - 1;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      return (current + delta + rowCount) % rowCount;
    });
  }

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        /*
         * The label is clipped, so hovering is the only way back to the whole
         * string for a sighted user — the same answer QuoteLineItemsTable's
         * `ProductControl` already reaches for. Screen readers are unaffected
         * either way: the text stays in the DOM and CSS truncation does not
         * touch the accessible name, which the button's own content still
         * supplies (content beats `title`).
         */
        title={selected ? selected.label : undefined}
        className={cx(
          /*
           * ⚠️ **`min-w-0` is what keeps a long value inside its column.**
           *
           * A Combobox is normally the control slot of a `Field`, and `Field`
           * is a grid — so the trigger is a GRID ITEM, and a grid item's
           * default `min-width: auto` floors its track at the item's own
           * min-content size. That size is not small: the label span is
           * `truncate`, i.e. `white-space: nowrap`, so its min-content size is
           * the ENTIRE string. `min-w-0` on the span only removes the span's
           * floor inside this flex box; it does not shrink what the span
           * contributes to the button's min-content, and the button then
           * hands that number up to the track.
           *
           * `w-full` cannot save it either: against an `auto` track the
           * percentage is cyclic, so it is treated as `auto` while the track
           * is being sized from content. Measured in production: a 296px
           * column, a 367px trigger, 71px of it lying across the Contact
           * field beside it.
           *
           * With `min-width: 0` the track's floor drops to zero, the track is
           * clamped to the 296px the grid actually has, `w-full` resolves
           * against that, and the span ellipses inside it. The chevron is
           * `shrink-0` and sits after the span, so it holds its place at the
           * right edge instead of being pushed out.
           *
           * `InlineField` already puts `min-w-0` on its own wrapper div for
           * exactly this reason; that is why it never showed the bug.
           */
          'flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-left text-sm max-sm:h-11',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'hover:border-hairline-strong',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-subtle',
          'aria-invalid:border-danger',
        )}
      >
        <span className={cx('min-w-0 flex-1 truncate', selected ? 'text-fg' : 'text-fg-subtle')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronIcon />
      </button>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              // `left` is clamped to the viewport by `place` and the measure
              // effect above, which rerun on scroll, resize and content change
              // — so the width bounds live there too rather than half here and
              // half in those effects. `width: max-content`: the popover sizes
              // to its longest visible label rather than to the trigger, so a
              // long product name (e.g. "Sales - Business School Festivals: …")
              // has room to read in full. `minWidth` is the floor (never
              // narrower than the trigger, at least POPOVER_MIN_WIDTH),
              // `maxWidth` the viewport ceiling — the browser picks whatever
              // content size falls between the two, and the measure effect
              // above reads that back to correct `left`.
              left: rect.left,
              width: 'max-content',
              minWidth: rect.minWidth,
              maxWidth: rect.maxWidth,
              ...(rect.flip
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.top + 4 }),
            }}
            className="z-[60] overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
          >
            <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
              <SearchIcon />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={rowCount > 0 ? `${listId}-${active}` : undefined}
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
              />
            </div>

            {header && (
              <div
                /*
                 * Between the filter box and the list, so DOM order is the tab
                 * order: filter → header → out. Escape closes from here exactly
                 * as it does from the filter box, and a forward Tab off the END
                 * of the header leaves the popover as a Tab always has.
                 *
                 * 🔴 **IT MUST BE THE END, NOT ANY TAB — and this shipped wrong
                 * on 2026-09-11.** This handler is on the WRAPPER, so a keydown
                 * bubbles up from whichever control has focus. Closing on every
                 * forward Tab therefore closed the popover on the way from the
                 * first control to the second, and the browser never got to
                 * move focus: a two-segment header was reachable by pointer and
                 * the second segment was reachable by nothing at all.
                 *
                 * Measured in production on the project-scope pills: `Tab` from
                 * the filter box focused `Current`, and `Tab` again closed the
                 * popover without ever focusing `Previous`. Arrow keys do not
                 * help — `SegmentedControl` states in its own header that it
                 * uses `role="group"` with ordinary buttons and deliberately
                 * does NOT implement roving arrow navigation, because
                 * `radiogroup` "would promise arrow-key roving that these
                 * buttons do not". So Tab was the only path, and it was shut.
                 *
                 * ⚠️ The two contracts contradicted each other and NOTHING
                 * FAILED: `tsc` green, no console error, and the control looks
                 * perfect to anyone using a mouse. The header prop exists
                 * because "Tab inside the popover normally closes it, which
                 * would make anything here pointer-only" — and it reintroduced
                 * exactly that, one element further in.
                 *
                 * Queried live rather than cached: a header's contents are the
                 * caller's and may change between renders (a pill appearing, a
                 * control disabling). An empty header still closes, which is
                 * the old behaviour for a header nobody can focus into.
                 */
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    close();
                    return;
                  }
                  if (event.key !== 'Tab' || event.shiftKey) return;
                  const focusable = event.currentTarget.querySelectorAll<HTMLElement>(
                    'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
                  );
                  const last = focusable[focusable.length - 1];
                  if (last === undefined || event.target === last) tabOut(event);
                }}
                className="border-b border-border px-2.5 py-2"
              >
                {header}
              </div>
            )}

            <ul
              id={listId}
              ref={listRef}
              role="listbox"
              style={{ maxHeight: POPOVER_MAX_HEIGHT }}
              className="m-0 list-none overflow-y-auto p-1"
            >
              {/*
               * ⚠️ ABOVE the loading and no-match rows, not below them — it is
               * the row somebody reaches for when the list does NOT contain
               * what they want, which is exactly the moment they have typed a
               * term that matches nothing.
               *
               * `role="option"` and the shared `data-active` keep it inside the
               * listbox's own navigation rather than beside it; it is index 0,
               * and every option below carries `addNewRows` added to its index.
               */}
              {addNew && (
                <li>
                  <div
                    id={`${listId}-0`}
                    role="option"
                    aria-selected={false}
                    data-active={addNewIsActive}
                    onPointerDown={(event) => {
                      // Before the input's blur can close the popover, as below.
                      event.preventDefault();
                      close();
                      addNew.onSelect();
                    }}
                    onMouseEnter={() => setActiveIndex(0)}
                    className={cx(
                      // The wording carries the meaning. Neutral ink: the accent
                      // is reserved for primary actions, active nav and focus.
                      'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-fg',
                      addNewIsActive && 'bg-surface-hover',
                    )}
                  >
                    {/*
                     * The SPACER box. This row chooses nothing, so it gets no
                     * tick — but without the reserved width its label would sit
                     * 24px left of every option beneath it, which reads as a
                     * broken list rather than as a pinned row.
                     */}
                    <RowCheckbox />
                    <span className="min-w-0 flex-1 truncate">{addNew.label}</span>
                  </div>
                </li>
              )}

              {loading && (
                <li className="px-2 py-3 text-center text-xs text-fg-muted">Searching…</li>
              )}

              {/*
               * Counted excluding the always-present empty option: with a
               * no-match search the list would otherwise show "Unassigned"
               * alone, which reads as though it were a result.
               */}
              {!loading && term.trim() !== '' && realMatchCount === 0 && (
                <li className="px-2 py-3 text-center text-xs text-fg-muted">
                  No matches for “{term.trim()}”
                </li>
              )}

              {!loading &&
                visible.map((option, index) => {
                  const isSelected = option.value === value;
                  // Shifted by the pinned row, which is index 0 when present.
                  const rowIndex = index + addNewRows;
                  const isActive = rowIndex === active;
                  return (
                    <li key={option.value || '__empty'}>
                      <div
                        id={`${listId}-${rowIndex}`}
                        role="option"
                        aria-selected={isSelected}
                        data-active={isActive}
                        onPointerDown={(event) => {
                          // Commit before the input's blur can close the popover.
                          event.preventDefault();
                          commit(option);
                        }}
                        onMouseEnter={() => setActiveIndex(rowIndex)}
                        className={cx(
                          'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                          isActive ? 'bg-surface-hover text-fg' : 'text-fg-secondary',
                        )}
                      >
                        {/*
                         * The LEADING mark — owner's instruction 2026-09-13,
                         * replacing a tick at the END of the row.
                         *
                         * ⚠️ **A SQUARE BOX ON A SINGLE-SELECT IS DELIBERATE.**
                         * It is `aria-hidden` decoration, so nothing announces
                         * a checkbox and the tree still reads `option` +
                         * `aria-selected`; the shape is what makes this list
                         * and a `MultiCombobox` list one visual language. A
                         * radio here would put two selection idioms in one
                         * popover — read `RowCheckbox` before changing it.
                         *
                         * The indent is the same arithmetic as the other two
                         * lists: `px-2` + a 16px box + `gap-2`. Move any of the
                         * three and the popovers stop lining up.
                         */}
                        <RowCheckbox checked={isSelected} />
                        <span className="min-w-0 flex-1">
                          <span
                            className={cx(
                              'block truncate',
                              !option.value && 'text-fg-muted',
                              // A second signal beside the box, never the only
                              // one — the same pairing `MultiCombobox` uses.
                              isSelected && 'font-medium text-fg',
                            )}
                          >
                            {option.label}
                          </span>
                          {option.hint && (
                            <span className="block truncate text-xs text-fg-muted">
                              {option.hint}
                            </span>
                          )}
                        </span>
                      </div>
                    </li>
                  );
                })}
            </ul>

            {footnote && (
              <p className="border-t border-border px-2.5 py-1.5 text-2xs text-fg-muted">
                {footnote}
              </p>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
